import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { FileDown, FileCode2, Eye, Copy as CopyIcon } from "lucide-react";
import { PageTitle, Panel, Table, Field, TextInput, Select, Btn, AddButton, RowActions, EmptyState, Modal, StatCard } from "../components/ui";
import { useI18n } from "../i18n";
import { CL } from "../api";
import { CbamRulesBanner } from "../components/CbamRulesBanner";
import { CbamImportButtons } from "../components/CbamImportButtons";
import { CbamSupplierRequests } from "../components/CbamSupplierRequests";

const SECTORS = ["Çimento", "Demir-Çelik", "Alüminyum", "Gübre", "Elektrik", "Hidrojen"];
const YEARS = [2024, 2025, 2026, 2027];
const QUARTERS = ["Q1", "Q2", "Q3", "Q4"];
const FIELDS = ["cn_code", "product", "sector", "country", "quantity", "direct", "indirect", "carbon_price_paid", "data_source", "benchmark", "route"];
const empty = { cn_code: "", product: "", sector: "Demir-Çelik", country: "Türkiye", quantity: "", direct: "", indirect: "", carbon_price_paid: "", data_source: "actual", benchmark: "", route: "" };
const NUMERIC = ["quantity", "direct", "indirect", "carbon_price_paid", "benchmark"];

export default function CbamPage() {
  const { t } = useI18n();
  const [year, setYear] = useState(2026);
  const [quarter, setQuarter] = useState("Q1");
  const [rows, setRows] = useState([]);
  const [sum, setSum] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [xmlOpen, setXmlOpen] = useState(false);
  const [xmlText, setXmlText] = useState("");
  const [countries, setCountries] = useState([]);
  const [goods, setGoods] = useState([]);

  const reqId = useRef(0);
  const load = useCallback(() => {
    const id = ++reqId.current;
    const keep = (fn) => (d) => { if (id === reqId.current) fn(d); };
    CL.cbamList(year, quarter).then(keep(setRows)).catch(() => {});
    CL.cbamSummary(year, quarter).then(keep(setSum)).catch(() => {});
  }, [year, quarter]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { CL.cbamCountries().then(setCountries).catch(() => {}); }, []);
  useEffect(() => {
    if (!open || !form.country) return;
    CL.cbamGoods(form.country).then(setGoods).catch(() => setGoods([]));
  }, [open, form.country]);

  const up = (k) => (e) => { const v = e.target.value; setForm((p) => ({ ...p, [k]: v })); };
  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (r) => {
    setEditing(r);
    setForm(Object.fromEntries(FIELDS.map((k) => [k, r[k] ?? empty[k]])));
    setOpen(true);
  };

  const num = (v) => (v === "" || v == null ? 0 : Number(v));
  const save = async () => {
    const payload = Object.fromEntries(FIELDS.map((k) => [k, NUMERIC.includes(k) ? num(form[k]) : form[k]]));
    try {
      if (editing) await CL.cbamUpdate(editing.id, { ...payload, year: editing.year, quarter: editing.quarter });
      else await CL.cbamCreate(payload, year, quarter);
      setOpen(false); load(); toast.success(t("saved"));
    } catch { toast.error("Error"); }
  };
  const del = async (r) => { if (!window.confirm(t("confirmDelete"))) return; await CL.cbamDelete(r.id); load(); };

  const fetchOfficial = async () => {
    try {
      const res = await CL.cbamLookup(form.country, form.cn_code);
      const dv = res.default_value;
      const isDefault = form.data_source === "default";
      if (!dv && res.benchmark_actual == null) { toast.error(t("dvNotFound")); return; }
      setForm((p) => ({
        ...p,
        sector: dv?.sector || p.sector,
        product: p.product || dv?.description || "",
        route: dv?.route || p.route,
        direct: isDefault && dv ? dv.direct : p.direct,
        indirect: isDefault && dv ? dv.indirect : p.indirect,
        benchmark: (isDefault ? res.benchmark_default : res.benchmark_actual) ?? p.benchmark,
      }));
      toast.success(t("dvFilled"));
    } catch { toast.error(t("dvNotFound")); }
  };

  const download = async (kind) => {
    setBusy(true);
    try {
      const data = await CL.cbamExportBlob(kind, year, quarter);
      const mime = kind === "xml" ? "application/xml" : "application/pdf";
      const blob = new Blob([data], { type: mime });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `cbam-${year}-${quarter}.${kind}`;
      a.rel = "noopener"; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 20000);
      toast.success(`${kind.toUpperCase()} ${t("downloaded")}`);
    } catch { toast.error(t("exportError")); }
    finally { setBusy(false); }
  };

  const openXmlPreview = async () => {
    setBusy(true);
    try {
      const text = await CL.cbamExportXmlText(year, quarter);
      setXmlText(text);
      setXmlOpen(true);
    } catch { toast.error(t("xmlFetchError")); }
    finally { setBusy(false); }
  };

  const copyXml = async () => {
    try {
      await navigator.clipboard.writeText(xmlText);
      toast.success(t("cbamCopied"));
    } catch { toast.error(t("copyError")); }
  };

  const fy = editing?.year ?? year;
  const indirectCounts = ["Çimento", "Gübre"].includes(form.sector);
  const markup = form.data_source === "default" && fy >= 2026 ? (form.sector === "Gübre" ? 0.01 : fy === 2026 ? 0.1 : fy === 2027 ? 0.2 : 0.3) : 0;
  const intensity = fy >= 2026 ? num(form.direct) + (indirectCounts ? num(form.indirect) : 0) : num(form.direct) + num(form.indirect);
  const preview = form.quantity && (form.direct || form.indirect)
    ? (num(form.quantity) * intensity * (1 + markup)).toFixed(3) : null;

  return (
    <div data-testid="cbam-page">
      <PageTitle title={t("cbamTitle")} subtitle={t("cbamDesc")}
        action={
          <div className="flex items-center gap-2">
            <Select value={year} onChange={(e) => setYear(Number(e.target.value))} data-testid="cbam-year" className="w-28">
              {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
            </Select>
            <Select value={quarter} onChange={(e) => setQuarter(e.target.value)} data-testid="cbam-quarter" className="w-24">
              {QUARTERS.map((q) => <option key={q} value={q}>{q}</option>)}
            </Select>
          </div>
        } />

      <CbamRulesBanner sum={sum} />

      <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-4">
        <StatCard testId="cbam-total" label={t("totalEmbedded")} value={(sum?.total_embedded ?? 0).toLocaleString()} sub="tCO₂e" />
        <StatCard testId="cbam-faa" label={t("faaLabel")} value={(sum?.faa_total ?? 0).toLocaleString()} sub="tCO₂e" accent="#4b544c" />
        <StatCard testId="cbam-certs" label={t("estCertificates")} value={(sum?.estimated_certificates ?? 0).toLocaleString()} accent="#2f8f5b" />
        <StatCard testId="cbam-cost" label={t("netCost")} value={`€ ${(sum?.net_cost_eur ?? 0).toLocaleString()}`} accent="#e08a1e" />
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <AddButton label={t("add")} onClick={openNew} />
        <CbamImportButtons onDone={load} />
        <Btn variant="outline" onClick={() => download("pdf")} disabled={busy} data-testid="cbam-export-pdf"><FileDown className="h-4 w-4" />{t("exportPdf")}</Btn>
        <Btn variant="outline" onClick={openXmlPreview} disabled={busy} data-testid="cbam-preview-xml"><Eye className="h-4 w-4" />{t("xmlPreview")}</Btn>
        <Btn variant="outline" onClick={() => download("xml")} disabled={busy} data-testid="cbam-export-xml"><FileCode2 className="h-4 w-4" />{t("exportXml")}</Btn>
        <span className="text-xs text-[#8a8f83]">{t("xmlNote")}</span>
      </div>

      <Panel>
        {rows.length === 0 ? <EmptyState text={t("noData")} /> : (
          <Table head={[{ label: t("cnCode") }, { label: t("product") }, { label: t("origin") }, { label: t("dataSource") }, { label: t("quantityT"), right: true }, { label: t("embeddedTotal"), right: true }, { label: t("certsCol"), right: true }, { label: t("actions"), right: true }]}>
            {rows.map((r) => (
              <tr key={r.id} data-testid={`cbam-row-${r.id}`} className="hover:bg-[#fafbf6]">
                <td className="px-4 py-3 font-medium text-[#1b2420]">{r.cn_code || "—"}</td>
                <td className="px-4 py-3 text-[#4b544c]">{r.product || "—"}<div className="text-xs text-[#8a8f83]">{t(`sec_${r.sector}`)}</div></td>
                <td className="px-4 py-3 text-[#4b544c]">{r.country || "—"}</td>
                <td className="px-4 py-3 text-xs text-[#4b544c]">{r.data_source === "default" ? t("dsDefault") : t("dsActual")}</td>
                <td className="px-4 py-3 text-right text-[#4b544c]">{r.quantity?.toLocaleString?.() ?? r.quantity}</td>
                <td className="px-4 py-3 text-right text-[#4b544c]">{r.embedded_total?.toLocaleString?.() ?? r.embedded_total}</td>
                <td className="px-4 py-3 text-right font-medium text-[#14512f]" data-testid={`cbam-row-certs-${r.id}`}>{(r.certificates ?? 0).toLocaleString()}</td>
                <td className="px-4 py-3"><RowActions onEdit={() => openEdit(r)} onDelete={() => del(r)} /></td>
              </tr>
            ))}
          </Table>
        )}
      </Panel>

      <CbamSupplierRequests year={year} quarter={quarter} countries={countries} onApproved={load} />

      <Modal open={open} onClose={() => setOpen(false)} title={`CBAM ${year} ${quarter} — ${editing ? t("edit") : t("add")}`}
        footer={<><Btn variant="outline" onClick={() => setOpen(false)}>{t("cancel")}</Btn><Btn onClick={save} data-testid="cbam-save">{t("save")}</Btn></>}>
        <div className="grid grid-cols-2 gap-4">
          <Field label={t("origin")}>
            <Select data-testid="cbam-country" value={form.country} onChange={up("country")}>
              {!countries.includes(form.country) && form.country && <option value={form.country}>{form.country}</option>}
              {countries.map((c) => <option key={c} value={c}>{c}</option>)}
            </Select>
          </Field>
          <Field label={t("dataSource")}>
            <Select data-testid="cbam-data-source" value={form.data_source} onChange={up("data_source")}>
              <option value="actual">{t("dsActual")}</option>
              <option value="default">{t("dsDefault")}</option>
            </Select>
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Field label={t("cnCode")}>
            <TextInput data-testid="cbam-cn" list="cbam-cn-list" value={form.cn_code} onChange={up("cn_code")} placeholder="72083600" />
            <datalist id="cbam-cn-list">{goods.map((g) => <option key={g.cn + g.route} value={g.cn}>{g.cn_display} — {g.description}</option>)}</datalist>
          </Field>
          <Field label={t("sector")}><Select data-testid="cbam-sector" value={form.sector} onChange={up("sector")}>{SECTORS.map((s) => <option key={s} value={s}>{t(`sec_${s}`)}</option>)}</Select></Field>
        </div>
        <Btn variant="outline" onClick={fetchOfficial} disabled={!form.cn_code} data-testid="cbam-fetch-official" className="mb-4">{t("fetchOfficial")}</Btn>
        <Field label={t("product")}><TextInput data-testid="cbam-product" value={form.product} onChange={up("product")} /></Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label={t("quantityT")}><TextInput type="number" data-testid="cbam-quantity" value={form.quantity} onChange={up("quantity")} /></Field>
          <Field label={t("benchmarkBm")}><TextInput type="number" data-testid="cbam-benchmark" value={form.benchmark} onChange={up("benchmark")} /></Field>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <Field label={t("directIntensity")}><TextInput type="number" data-testid="cbam-direct" value={form.direct} onChange={up("direct")} /></Field>
          <Field label={t("indirectIntensity")}><TextInput type="number" data-testid="cbam-indirect" value={form.indirect} onChange={up("indirect")} /></Field>
          <Field label={t("carbonPricePaid")}><TextInput type="number" data-testid="cbam-price" value={form.carbon_price_paid} onChange={up("carbon_price_paid")} /></Field>
        </div>
        <p className="mb-3 text-xs text-[#8a8f83]">{t("markupNote")} {t("indirectNote")}</p>
        {preview && <div className="rounded-lg bg-[#eef6ef] px-4 py-3 text-sm text-[#14512f]" data-testid="cbam-preview">{t("chargeable")}: ≈ {Number(preview).toLocaleString()}</div>}
      </Modal>

      <Modal open={xmlOpen} onClose={() => setXmlOpen(false)} title={`CBAM XML — ${year} ${quarter}`}
        footer={<>
          <Btn variant="outline" onClick={() => setXmlOpen(false)}>{t("close")}</Btn>
          <Btn variant="outline" onClick={copyXml} data-testid="cbam-xml-copy"><CopyIcon className="h-4 w-4" />{t("cbamCopy")}</Btn>
          <Btn onClick={() => download("xml")} data-testid="cbam-xml-download-inline"><FileCode2 className="h-4 w-4" />{t("download")}</Btn>
        </>}>
        <div className="text-xs text-[#6b7268] mb-2">
          {t("xmlHelp")}
        </div>
        <pre data-testid="cbam-xml-preview"
             className="max-h-[60vh] overflow-auto rounded-lg bg-[#0f1512] p-4 text-xs text-[#c8e6c9] whitespace-pre-wrap break-all">
{xmlText}
        </pre>
      </Modal>
    </div>
  );
}
